// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — outbound rpc-terminal serialization (ABB-S3)
// ═══════════════════════════════════════════════════════════════════════════
//
// PURE terminal-frame routing + serialization extracted from agentBridgeClient.ts
// (round-4: the client crossed the 1000-line soft-limit after the 4×P2 fixes —
// CLAUDE.md "Контроль размера файлов"). NO behaviour change: the client's thin
// `rawSend` wrappers now delegate the build/route/serialize/fallback logic here.
//
// The single responsibility: given an inbound rpc frame + a handler outcome,
// produce the JSON TEXT of the correct terminal frame, routed by METHOD to the
// right wire (export.dxf → export-* frames so the companion's `ExportPump`
// matches them by correlationId+epoch; every other method → rpc-result/rpc-error),
// and FALL BACK to a minimal, guaranteed-serializable terminal when an untrusted
// handler result/detail (BigInt, circular ref) makes `JSON.stringify` throw — so
// the companion is NEVER left waiting out its deadline.
//
// LEAF module: imports ONLY the codec builders/types. No socket, no editorStore,
// no node-companion (ПРИОРИТЕТ #2).
// ═══════════════════════════════════════════════════════════════════════════

import {
  buildExportCompleteFrame,
  buildExportErrorFrame,
  buildRpcErrorFrame,
  buildRpcResultFrame,
  type InboundRpcFrame,
  type OutboundFrame,
  serializeFrame,
} from "./wsFrameCodec";

/** A successful handler outcome's terminal-relevant fields. */
export type SuccessOutcome = { batchHash?: string; result?: unknown; outcomeId?: string };

/** A failed handler outcome's terminal-relevant fields. */
export type FailureOutcome = {
  batchHash?: string;
  code: string;
  message: string;
  detail?: unknown;
  /**
   * Gate #4 / FIX#2 — a provisional outcome handle for the ERROR terminal,
   * SYMMETRIC with `SuccessOutcome.outcomeId`. Wired through to
   * `buildRpcErrorFrame` so a failure terminal CAN carry an outcomeId (the
   * companion already reads it on `rpc-error` — `wsServer.ts` onRpcReply). No
   * producer sets it yet (the cached-error-replay path is future work), but the
   * field + plumbing exist symmetrically so it is never silently dropped once
   * one does.
   */
  outcomeId?: string;
};

/**
 * [round-3 P2 #3] Read the terminal export-complete accounting from a SUCCESSFUL
 * `export.dxf` handler outcome's `result`. The wired S6 producer streams the
 * `export-chunk*` frames itself and returns `result = { byteLength, snapshotRevision? }`
 * (byteLength = the sum of DECODED chunk bytes; the companion ABORTS a non-integer /
 * negative / mismatched byteLength — `exportBroker.ts`:368-383). Returns `null` for a
 * malformed/absent accounting (then the client fails the export on the export wire
 * rather than send a bogus complete). `snapshotRevision` defaults to `null` when absent
 * (a document with no revision counter); a numeric one is preserved.
 */
export function readExportCompleteAccounting(
  result: unknown,
): { byteLength: number; snapshotRevision: number | null } | null {
  if (typeof result !== "object" || result === null) {
    return null;
  }
  const body = result as { byteLength?: unknown; snapshotRevision?: unknown };
  if (
    typeof body.byteLength !== "number" ||
    !Number.isInteger(body.byteLength) ||
    body.byteLength < 0
  ) {
    return null;
  }
  const snapshotRevision = typeof body.snapshotRevision === "number" ? body.snapshotRevision : null;
  return { byteLength: body.byteLength, snapshotRevision };
}

/**
 * [round-3 P2 #4] The minimal, GUARANTEED-serializable fallback terminal for an rpc
 * whose primary terminal failed to JSON-encode. Built from ONLY a primitive string
 * message + the (string) correlationId + (number) sessionEpoch — never the untrusted
 * result/detail — so `JSON.stringify` here cannot throw. Routed by method to the
 * correct wire (export-error for export.dxf so the companion's pump fails the export
 * fast; rpc-error otherwise).
 */
export function serializeFallbackTerminal(frame: InboundRpcFrame): string {
  if (frame.method === "export.dxf") {
    return serializeFrame(
      buildExportErrorFrame({
        correlationId: frame.correlationId,
        sessionEpoch: frame.sessionEpoch,
        code: "browser-dispatch-error",
        message: "export.dxf reply could not be serialized (non-serializable outcome)",
      }),
    );
  }
  return serializeFrame(
    buildRpcErrorFrame({
      correlationId: frame.correlationId,
      sessionEpoch: frame.sessionEpoch,
      code: "browser-dispatch-error",
      message: "rpc reply could not be serialized (non-serializable outcome)",
    }),
  );
}

/**
 * [round-3 P2 #4] Build → serialize a handler-OUTCOME terminal, guarding the JSON-encode
 * of an UNTRUSTED handler result/detail. A handler may return a non-serializable
 * `result`/`detail` (BigInt, a circular ref); `JSON.stringify` then THROWS. On an encode
 * failure (or a builder that touches a poison getter — the thunk runs INSIDE the try) we
 * return the minimal fallback terminal keyed by method so the companion is never left
 * hanging. The fallback uses no untrusted value → it cannot itself throw.
 */
function serializeRpcTerminal(frame: InboundRpcFrame, build: () => OutboundFrame): string {
  try {
    return serializeFrame(build());
  } catch {
    return serializeFallbackTerminal(frame);
  }
}

/**
 * [round-3 P2 #3] Serialize a SUCCESS terminal for an rpc, routed by METHOD to the correct
 * wire. An `export.dxf` success replies `export-complete` on the EXPORT wire (read
 * `byteLength` + `snapshotRevision` from the handler's success `result`); the companion's
 * `ExportPump.onComplete` matches it by correlationId + epoch and seals the body with the
 * success trailer (`exportBroker.ts`:349-385). An `rpc-result` carrying an export
 * correlationId would NOT be matched (→ `onRpcReply` → no command slot → drop → HTTP hang),
 * so the success path MUST go on the export wire too. A malformed/absent export accounting
 * body is itself an export FAILURE → fail-fast on the export wire (export-error), never a
 * bogus complete. Every other method (`command.run`/`state.read`/`manifest.read`) replies
 * `rpc-result`.
 */
export function serializeSuccessTerminal(
  frame: InboundRpcFrame,
  outcome: SuccessOutcome,
): string {
  if (frame.method === "export.dxf") {
    const accounting = readExportCompleteAccounting(outcome.result);
    if (accounting === null) {
      return serializeFailureTerminal(frame, {
        code: "browser-dispatch-error",
        message:
          "export.dxf success outcome is missing a non-negative integer byteLength accounting",
      });
    }
    return serializeRpcTerminal(frame, () =>
      buildExportCompleteFrame({
        correlationId: frame.correlationId,
        sessionEpoch: frame.sessionEpoch,
        byteLength: accounting.byteLength,
        snapshotRevision: accounting.snapshotRevision,
      }),
    );
  }
  return serializeRpcTerminal(frame, () =>
    buildRpcResultFrame({
      correlationId: frame.correlationId,
      sessionEpoch: frame.sessionEpoch,
      batchHash: outcome.batchHash,
      result: outcome.result,
      outcomeId: outcome.outcomeId,
    }),
  );
}

/**
 * [round-2 finding C] Serialize a FAILURE terminal for an rpc, routed by METHOD to the
 * correct wire. An `export.dxf` failure (unwired, a wired handler returning `{ok:false}`,
 * OR a wired handler that THREW) MUST reply `export-error` — the companion's
 * `ExportPump.onExportFrame` consumes ONLY `export-error`/`export-chunk`/`export-complete`
 * for an export correlation, so an `rpc-error` carrying an export correlationId is NEVER
 * matched (`onRpcReply` finds no command slot → drop) and the HTTP export HANGS until the
 * stall/overall deadline. EVERY other method (`command.run`/`state.read`/`manifest.read`)
 * uses the generic `rpc-error` path.
 */
export function serializeFailureTerminal(
  frame: InboundRpcFrame,
  outcome: FailureOutcome,
): string {
  if (frame.method === "export.dxf") {
    return serializeRpcTerminal(frame, () =>
      buildExportErrorFrame({
        correlationId: frame.correlationId,
        sessionEpoch: frame.sessionEpoch,
        code: outcome.code,
        message: outcome.message,
      }),
    );
  }
  return serializeRpcTerminal(frame, () =>
    buildRpcErrorFrame({
      correlationId: frame.correlationId,
      sessionEpoch: frame.sessionEpoch,
      batchHash: outcome.batchHash,
      code: outcome.code,
      message: outcome.message,
      detail: outcome.detail,
      // Gate #4 / FIX#2 — carry the failure outcomeId SYMMETRIC with the success
      // terminal (serializeSuccessTerminal passes `outcome.outcomeId`). Absent
      // for an unwired/thrown failure → omitted by the builder.
      outcomeId: outcome.outcomeId,
    }),
  );
}
